import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { ProvidersProvider } from "./contexts/ProvidersContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ReauthProvider } from "./components/ReauthGate";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import AppShell from "./components/layout/AppShell";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Patients from "./pages/Patients";
import PatientDetail from "./pages/PatientDetail";
import InitialExamEditor from "./pages/clinical/InitialExamEditor";
import FollowUpNoteEditor from "./pages/clinical/FollowUpNoteEditor";
import TreatmentPlanEditor from "./pages/clinical/TreatmentPlanEditor";
import ReExamEditor from "./pages/clinical/ReExamEditor";
import Scheduling from "./pages/Scheduling";
import FlowBoardPage from "./pages/scheduling/FlowBoardPage";
import ProviderQueuePage from "./pages/scheduling/ProviderQueuePage";
import CheckoutPage from "./pages/scheduling/CheckoutPage";
import Notifications from "./pages/Notifications";
import Security from "./pages/Security";
import AuditLog from "./pages/AuditLog";
import Compliance from "./pages/Compliance";
import Privacy from "./pages/Privacy";
import SecurityConfig from "./pages/SecurityConfig";
import PasswordReset from "./pages/PasswordReset";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminRolesPage from "./pages/admin/AdminRolesPage";
import AccessHistoryPage from "./pages/admin/AccessHistoryPage";
import PortalShell from "./portal/PortalShell";
import PortalOverview from "./portal/PortalOverview";
import PortalStatements from "./portal/PortalStatements";
import PortalLogin from "./portal/PortalLogin";
import PortalBook from "./portal/PortalBook";
import PortalQuestionnaires from "./portal/PortalQuestionnaires";
import PortalQuestionnaireDetail from "./portal/PortalQuestionnaireDetail";
import Kiosk from "./pages/Kiosk";
import SmsSettings from "./pages/settings/SmsSettings";
import EmailSettings from "./pages/settings/EmailSettings";
import GoogleAuthSettings from "./pages/settings/GoogleAuthSettings";
import AITemplatesPage from "./pages/settings/AITemplatesPage";
import AIModelsPage from "./pages/settings/AIModelsPage";
import SmsInbox from "./pages/communications/SmsInbox";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import BookingRequestsQueue from "./pages/scheduling/BookingRequestsQueue";
import Elevation from "./pages/Elevation";
import ThemePreview from "./pages/ThemePreview";
import ClinicSettings from "./pages/ClinicSettings";
import AppointmentTypesPage from "./pages/AppointmentTypesPage";
import RoomsManagerPage from "./pages/settings/RoomsManagerPage";
import ClearinghouseSettingsPage from "./pages/settings/ClearinghouseSettingsPage";
import PaymentsSettings from "./pages/settings/PaymentsSettings";
import PayersPage from "./pages/PayersPage";
import FeeSchedulesPage from "./pages/FeeSchedulesPage";
import BillingDashboard from "./pages/billing/BillingDashboard";
import InvoicesList from "./pages/billing/InvoicesList";
import InvoiceDetail from "./pages/billing/InvoiceDetail";
import PatientLedgerPage from "./pages/billing/PatientLedgerPage";
import ClaimsQueue from "./pages/billing/ClaimsQueue";
import ClaimDetail from "./pages/billing/ClaimDetail";
import RemittancePosting from "./pages/billing/RemittancePosting";
import RemittanceImport from "./pages/billing/RemittanceImport";
import RemittanceDetail from "./pages/billing/RemittanceDetail";
import DenialsQueue from "./pages/billing/DenialsQueue";
import ArAgingReport from "./pages/billing/ArAgingReport";
import ReportsLandingPage from "./pages/reports/ReportsLandingPage";
import ReportViewer from "./pages/reports/ReportViewer";
import "./App.css";

function Shell({ children, roles }) {
  return (
    <ProtectedRoute roles={roles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PermissionsProvider>
          <ProvidersProvider>
            <ReauthProvider>
            <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/password-reset" element={<PasswordReset />} />
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
              <Route path="/kiosk" element={<Kiosk />} />

              {/* ----- Patient portal (role=patient only) ----- */}
              <Route
                path="/portal"
                element={
                  <ProtectedRoute portal>
                    <PortalShell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<PortalOverview />} />
                <Route path="book" element={<PortalBook />} />
                <Route path="questionnaires" element={<PortalQuestionnaires />} />
                <Route path="questionnaires/:id" element={<PortalQuestionnaireDetail />} />
                <Route path="statements" element={<PortalStatements />} />
              </Route>

              <Route path="/" element={<Shell><Dashboard /></Shell>} />
              <Route path="/patients" element={<Shell><Patients /></Shell>} />
              <Route path="/patients/:id" element={<Shell><PatientDetail /></Shell>} />
              <Route path="/patients/:pid/clinical/exams/:eid" element={<Shell roles={["admin", "doctor", "staff"]}><InitialExamEditor /></Shell>} />
              <Route path="/patients/:pid/clinical/follow-up/:nid" element={<Shell roles={["admin", "doctor", "staff"]}><FollowUpNoteEditor /></Shell>} />
              <Route path="/patients/:pid/clinical/treatment-plans/:tpid" element={<Shell roles={["admin", "doctor", "staff"]}><TreatmentPlanEditor /></Shell>} />
              <Route path="/patients/:pid/clinical/re-exams/:rid" element={<Shell roles={["admin", "doctor", "staff"]}><ReExamEditor /></Shell>} />
              <Route path="/scheduling" element={<Shell><Scheduling /></Shell>} />
              <Route path="/scheduling/flow-board" element={<Shell roles={["admin", "doctor", "staff"]}><FlowBoardPage /></Shell>} />
              <Route path="/scheduling/provider-queue" element={<Shell roles={["admin", "doctor", "staff"]}><ProviderQueuePage /></Shell>} />
              <Route path="/scheduling/checkout" element={<Shell roles={["admin", "doctor", "staff"]}><CheckoutPage /></Shell>} />
              <Route path="/settings/clinic" element={<Shell roles={["admin"]}><ClinicSettings /></Shell>} />
              <Route path="/settings/appointment-types" element={<Shell roles={["admin"]}><AppointmentTypesPage /></Shell>} />
              <Route path="/settings/rooms" element={<Shell roles={["admin"]}><RoomsManagerPage /></Shell>} />
              <Route path="/settings/payers" element={<Shell roles={["admin"]}><PayersPage /></Shell>} />
              <Route path="/settings/fee-schedules" element={<Shell roles={["admin"]}><FeeSchedulesPage /></Shell>} />
              <Route path="/settings/clearinghouse" element={<Shell roles={["admin"]}><ClearinghouseSettingsPage /></Shell>} />
              <Route path="/settings/payments" element={<Shell roles={["admin"]}><PaymentsSettings /></Shell>} />
              <Route path="/settings/sms" element={<Shell roles={["admin"]}><SmsSettings /></Shell>} />
              <Route path="/settings/email" element={<Shell roles={["admin"]}><EmailSettings /></Shell>} />
              <Route path="/settings/ai-templates" element={<Shell roles={["admin"]}><AITemplatesPage /></Shell>} />
              <Route path="/settings/ai-models" element={<Shell roles={["admin"]}><AIModelsPage /></Shell>} />
              <Route path="/settings/google" element={<Shell roles={["admin"]}><GoogleAuthSettings /></Shell>} />
              <Route path="/communications/sms" element={<Shell roles={["admin", "staff"]}><SmsInbox /></Shell>} />
              <Route path="/scheduling/booking-requests" element={<Shell roles={["admin", "staff", "doctor"]}><BookingRequestsQueue /></Shell>} />
              <Route path="/billing" element={<Shell roles={["admin", "doctor", "staff"]}><BillingDashboard /></Shell>} />
              <Route path="/billing/invoices" element={<Shell roles={["admin", "doctor", "staff"]}><InvoicesList /></Shell>} />
              <Route path="/billing/invoices/:id" element={<Shell roles={["admin", "doctor", "staff"]}><InvoiceDetail /></Shell>} />
              <Route path="/billing/patients/:id/ledger" element={<Shell roles={["admin", "doctor", "staff"]}><PatientLedgerPage /></Shell>} />
              <Route path="/billing/claims" element={<Shell roles={["admin", "doctor", "staff"]}><ClaimsQueue /></Shell>} />
              <Route path="/billing/claims/:id" element={<Shell roles={["admin", "doctor", "staff"]}><ClaimDetail /></Shell>} />
              <Route path="/billing/remittances/new" element={<Shell roles={["admin", "staff"]}><RemittancePosting /></Shell>} />
              <Route path="/billing/remittances/import" element={<Shell roles={["admin", "staff"]}><RemittanceImport /></Shell>} />
              <Route path="/billing/remittances/:id" element={<Shell roles={["admin", "doctor", "staff"]}><RemittanceDetail /></Shell>} />
              <Route path="/billing/denials" element={<Shell roles={["admin", "doctor", "staff"]}><DenialsQueue /></Shell>} />
              <Route path="/billing/ar-aging" element={<Shell roles={["admin", "doctor", "staff"]}><ArAgingReport /></Shell>} />
              <Route path="/reports" element={<Shell roles={["admin", "doctor", "staff"]}><ReportsLandingPage /></Shell>} />
              <Route path="/reports/:name" element={<Shell roles={["admin", "doctor", "staff"]}><ReportViewer /></Shell>} />
              <Route path="/appointments" element={<Navigate to="/scheduling" replace />} />
              <Route path="/calendar" element={<Navigate to="/scheduling" replace />} />
              <Route path="/notifications" element={<Shell roles={["admin", "staff"]}><Notifications /></Shell>} />
              <Route path="/audit-log" element={<Shell roles={["admin"]}><AuditLog /></Shell>} />
              <Route path="/compliance" element={<Shell roles={["admin"]}><Compliance /></Shell>} />
              <Route path="/privacy" element={<Shell roles={["admin"]}><Privacy /></Shell>} />
              <Route path="/security-config" element={<Shell roles={["admin"]}><SecurityConfig /></Shell>} />
              <Route path="/admin/users" element={<Shell roles={["admin"]}><AdminUsersPage /></Shell>} />
              <Route path="/admin/roles" element={<Shell roles={["admin"]}><AdminRolesPage /></Shell>} />
              <Route path="/admin/access-history" element={<Shell roles={["admin"]}><AccessHistoryPage /></Shell>} />
              <Route path="/access-review" element={<Navigate to="/admin/access-history" replace />} />
              <Route path="/elevation" element={<Shell><Elevation /></Shell>} />
              <Route path="/security" element={<Shell><Security /></Shell>} />
              <Route path="/account" element={<Shell><Security /></Shell>} />
              <Route path="/settings/theme-preview" element={<Shell><ThemePreview /></Shell>} />
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
          </ReauthProvider>
          </ProvidersProvider>
        </PermissionsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
